import React, { useRef, useEffect, useState } from 'react';
import { EnhancedChatMessages } from 'src/editor/content-creation/talk-to-journey/enhanced-messages';
import { PitchChatMessages } from 'src/editor/content-creation/talk-to-journey/pitch-chat-messages';
import { PitchTalkToJourneySettingsPanelContent } from 'src/editor/content-creation/talk-to-journey/pitch-settings-panel-content';
import { ControlPanelShell } from 'src/editor/content-creation/components/control-panel/shell';
import { Journey } from 'src/common/interfaces/journey.interface';
import classNames from 'classnames';
import { useEnhancedChatStore } from 'src/editor/content-creation/talk-to-journey/enhanced-store';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journey: Journey;
  buttonRef: React.RefObject<HTMLElement>;
}

export const EditorHeaderAiAssistantPopover = ({ isOpen, onClose, journey, buttonRef }: Props) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { initializeFromJourney } = useEnhancedChatStore();

  // Initialize the enhanced chat store with journey data when popover opens
  useEffect(() => {
    if (isOpen && journey.is_neue) {
      initializeFromJourney(journey);
    }
  }, [isOpen, journey, initializeFromJourney]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClick(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose, buttonRef]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: buttonRect.bottom + 8, // 8px gap below button
        left: Math.max(0, buttonRect.left - 200), // Align to left edge, but don't go off-screen
      });
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return (
    <>
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden"
      style={{
        top: position.top,
        left: position.left,
        width: '400px',
        height: '600px',
      }}
    >
      {journey.is_neue ? (
        <EnhancedChatMessages
          opened={isOpen}
          journeyUUID={journey.uuid}
          onDialogClose={onClose}
          journey={journey}
          inEditor={true}
        />
      ) : (
        <PitchChatMessages
          opened={isOpen}
          journeyUUID={journey.uuid}
          onDialogClose={onClose}
          isPitchMode={true}
          assistantName="Journey Assistant"
          onNavigateToSection={(sectionId) => {
            console.log('Navigate to section:', sectionId);
          }}
          onNavigateToStep={(stepId) => {
            console.log('Navigate to step:', stepId);
          }}
          journey={journey}
        />
      )}
    </div>
      
      {/* Settings panel for pitch mode */}
      {!journey.is_neue && (
        <div
          className={classNames(
            'fixed z-50 transition-opacity duration-400 pointer-events-auto',
            {
              'opacity-0': !isOpen,
              'opacity-100': isOpen,
            }
          )}
          style={{
            top: position.top,
            left: position.left - 280, // Position to the left of the chat panel
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200">
            <ControlPanelShell
              active={isOpen}
              title='Edit chatbot'
              subtitle='Allow recipients to talk to a bot about your Pitch and its content'
            >
              <PitchTalkToJourneySettingsPanelContent />
            </ControlPanelShell>
          </div>
        </div>
      )}
    </>
  );
}; 